<script lang="ts">
	//Design system components
	import {
		Button,
		Container,
		Modal,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		resetWizardItems,
		updateWizardStepOnPrevNext,
		updateWizardStepOnWizardItem,
	} from '../Lists/list-helpers';
	// Types
	import { deepCopy } from 'src/helpers/utilities';
	import ExitConfirmationModal from '../ExitConfirmationModal.svelte';
	import type { ExtendEditableObject } from 'src/types/EditableObject';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showModal,
		title,
		callback,
		disabled = false,
		validation,
		wrapWithContainer = true;

	export let fromWifiBatch = false;
	export let fromQuickActions = false;

	export let editableObject: ExtendEditableObject;

	export let wizardItems;
	export let wizardContentClasses = '';

	const clonedThermostat = !fromWifiBatch
		? editableObject?.thermostat
			? deepCopy(editableObject?.thermostat)
			: deepCopy(editableObject)
		: {};
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let activeStep = wizardItems[0];
	const dispatch = createEventDispatcher();

	const previousButton = $format('button_back');

	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};
	$: if (showModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}

	$: isLastStepWizard = activeStep.activeIndex === Object.keys(wizardItems).length - 1;
	$: isFirstStep = activeStep.activeIndex === 0;
	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');
	const updateIndex = () => {
		editableObject.activeIndex = activeStep.activeIndex;
	};
	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };
		updateIndex();
	};

	let showCondition = false;

	const resetEditableObjectFromQuickActions = () => {
		if (fromQuickActions) {
			editableObject.selectedDevices = [];
			editableObject.selectedAction = undefined;
			editableObject.disabled = true;
		}
	};

	const onCloseEditModal = () => {
		showModal = false;
		resetEditableObjectFromQuickActions();
		wizardItems = resetWizardItems(wizardItems);
		activeStep = wizardItems[0];
		updateIndex();
	};

	const onFinishCardEdit = () => {
		callback();
		onCloseEditModal();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };
		updateIndex();
	};
</script>

<Modal
	on:close={() => {
		if (
			!fromWifiBatch &&
			JSON.stringify(editableObject?.thermostat ? editableObject?.thermostat : editableObject) !==
				JSON.stringify(clonedThermostat)
		) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			showModal = false;
			showCondition = false;
			onCloseEditModal();
			dispatch('close');
		}
	}}
	{showModal}
	{title}
	hideSlotFooter={showCondition}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					onClickWizardItem(i);
				}}
				active={item.active}
				title={item.title}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div class:flex-overflow-hidden={showCondition} slot="content">
		<WizardContainer activeStepIndex={activeStep.activeIndex} adaptiveHeight>
			{#each wizardItems as item, i (i)}
				<WizardContent active={activeStep.activeIndex === i} class={wizardContentClasses}>
					{#if wrapWithContainer}
						<Container noScroll padding="0">
							{#if item.subtitle}
								<Text element="h2" class="text-capitalize mb-m">
									{item.subtitle}
								</Text>
							{/if}
							<div>
								<svelte:component this={item.component} bind:editableObject />
							</div>
						</Container>
					{:else}
						{#if item.subtitle}
							<Text element="h2" class="text-capitalize mb-m">
								{item.subtitle}
							</Text>
						{/if}
						<div>
							<svelte:component this={item.component} bind:editableObject />
						</div>
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if !isFirstStep}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					onClickPrevNextButton('prev');
				}}
			>
				{previousButton}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isLastStepWizard
				? disabled
				: validation && validation[activeStep.activeIndex + 1]?.disabled}
			loading={editableObject.loading}
			on:click={() => {
				if (isLastStepWizard) {
					onFinishCardEdit();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

{#if !fromWifiBatch}
	<ExitConfirmationModal
		type={$format('the_thermostat', { default: 'the thermostat' })}
		{exitConfirmationModalState}
		{showExitConfirmationModal}
		showAddEditModal={showModal}
		on:close={(e) => {
			exitConfirmationModalState = e.detail.exitConfirmationModalState;
			showExitConfirmationModal = e.detail.showExitConfirmationModal;
		}}
		on:confirm={(e) => {
			showExitConfirmationModal = e.detail.showExitConfirmationModal;
			showModal = e.detail.showAddEditModal;
			resetEditableObjectFromQuickActions();
		}}
	/>
{/if}
